
import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { updateUserProfile } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Save, UserRound } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface ProfilePersonalInfoProps {
  userId?: string;
  profileData: any;
  email?: string;
}

const personalInfoSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  phone: z.string().optional(),
  address: z.string().optional(),
});

type PersonalInfoValues = z.infer<typeof personalInfoSchema>;

export const ProfilePersonalInfo: React.FC<ProfilePersonalInfoProps> = ({
  userId,
  profileData,
  email
}) => {
  const { t } = useTranslation(["profile"]);
  
  const form = useForm<PersonalInfoValues>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      first_name: profileData?.first_name || "",
      last_name: profileData?.last_name || "",
      phone: profileData?.phone || "",
      address: profileData?.address || "",
    },
  });

  // Brouillon persisté par utilisateur — restauration silencieuse au montage
  // de l'onglet (aucun effet reseed concurrent ici : profileData est chargé
  // avant le montage grâce au gate isLoading du parent).
  const { clearDraft } = useFormDraft("profile-personal", form, {
    entityId: userId ?? null,
    isEmpty: (v) => !v.first_name && !v.last_name && !v.phone && !v.address,
  });

  const onSubmit = async (data: PersonalInfoValues) => {
    if (!userId) {
      toast.error(t("errors.notAuthenticated", { ns: "profile" }));
      return;
    }

    try {
      // updateUserProfile renvoie un booléen : le déstructurer en { error }
      // donnait toujours `undefined`, donc un succès systématique.
      const ok = await updateUserProfile(userId, data);
      if (ok) {
        clearDraft();
        toast.success(t("success.profileUpdated", { ns: "profile" }));
      } else {
        toast.error(t("errors.updateFailed", { ns: "profile" }));
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(t("errors.updateFailed", { ns: "profile" }));
    }
  };

  return (
    <Card className="athlete-profile-card border shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle>{t("personal.title", { ns: "profile" })}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("personal.firstName", { ns: "profile" })}</FormLabel>
                    <FormControl>
                      <div className="relative"><UserRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-10" placeholder={t("personal.firstNamePlaceholder", { ns: "profile" })} {...field} /></div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("personal.lastName", { ns: "profile" })}</FormLabel>
                    <FormControl>
                      <div className="relative"><UserRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-10" placeholder={t("personal.lastNamePlaceholder", { ns: "profile" })} {...field} /></div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("personal.phone", { ns: "profile" })}</FormLabel>
                    <FormControl>
                      <div className="relative"><Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-10" placeholder={t("personal.phonePlaceholder", { ns: "profile" })} {...field} /></div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("personal.address", { ns: "profile" })}</FormLabel>
                  <FormControl>
                    <div className="relative"><MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-10" placeholder={t("personal.addressPlaceholder", { ns: "profile" })} {...field} /></div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {email && (
              <div className="space-y-2">
                <FormLabel>{t("personal.email", { ns: "profile" })}</FormLabel>
                <div className="relative"><Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-10" value={email} readOnly /></div>
              </div>
            )}
            <div className="flex justify-end">
              <Button type="submit" className="w-full sm:w-auto"><Save />{t("personal.saveChanges", { ns: "profile" })}</Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
